import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, TrendingUp, Play, Flame, Users, UserPlus, UserCheck, Activity, Clock, ClipboardCheck } from "lucide-react";

const TABS = [
  { key: "overall", label: "Overall", icon: Trophy,
    caption: "Report card score — each show's latest admin report card, with views, subscribers, members, followers, engagement and watch time each compared against the best show and averaged (0–100)." },
  { key: "views", label: "Most Watched", icon: Play, field: "view_count", short: "Views",
    caption: "Ranked by views on each show's latest report card." },
  { key: "subscribers", label: "Subscribers", icon: UserPlus, field: "subscriber_count", short: "Subs",
    caption: "Ranked by subscribers on each show's latest report card." },
  { key: "members", label: "Members", icon: UserCheck, field: "member_count", short: "Members",
    caption: "Ranked by members (channel members / paid supporters) on each show's latest report card." },
  { key: "followers", label: "Followers", icon: Users, field: "follower_count", short: "Followers",
    caption: "Ranked by followers across platforms on each show's latest report card." },
  { key: "engagement", label: "Engagement", icon: Activity, field: "engagement_rate", short: "Engage", pct: true,
    caption: "Ranked by engagement rate on each show's latest report card." },
  { key: "watch_time", label: "Watch Time", icon: Clock, field: "watch_time_hours", short: "Hours",
    caption: "Ranked by watch-time hours on each show's latest report card." },
];
const STAT_COLS = ["views", "subscribers", "followers"];

const CATEGORY_COLORS = {
  Talk: "#007AFF", Music: "#FF3B30", Sports: "#32D74B", Podcast: "#FFD60A",
  News: "#AF52DE", Comedy: "#FF9500", Interview: "#5AC8FA", "Live Event": "#FF2D92",
};

function medalColor(pos) {
  if (pos === 0) return "#FFD60A";
  if (pos === 1) return "#C0C6CC";
  if (pos === 2) return "#CD7F32";
  return "var(--ff-text-dim)";
}

export function formatCompact(n) {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return String(Math.round(n * 10) / 10);
}

const fmtMetric = (tab, v) => (v == null ? "—" : tab.pct ? `${Math.round(v * 10) / 10}%` : formatCompact(v));

export function reportLabel(r) {
  if (!r.report_period_end) return "";
  const end = new Date(`${r.report_period_end}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${r.report_period_type ? `${r.report_period_type} report` : "Report"} · ending ${end}`;
}

export default function Leaderboard() {
  const [sort, setSort] = useState("overall");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/leaderboard?sort=${sort}&limit=25`);
        if (alive) setRows(data.rows || []);
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [sort]);

  const active = TABS.find((t) => t.key === sort) || TABS[0];

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="leaderboard-page">
      <div className="flex items-center gap-3 mb-3">
        <TrendingUp className="text-[var(--ff-red)]" size={20} />
        <div className="label-eyebrow text-[var(--ff-red)]">▮ SHOW LEADERBOARD</div>
      </div>
      <h1 className="font-display text-5xl lg:text-6xl mb-4" data-testid="leaderboard-title">TOP OF THE NETWORK.</h1>
      <p className="text-[var(--ff-text-dim)] max-w-2xl mb-10 flex items-start gap-2" data-testid="leaderboard-subtitle">
        <ClipboardCheck size={18} className="text-[var(--ff-red)] shrink-0 mt-0.5" />
        Ranked by each show&apos;s latest official report card from Freshy Fresh management. Collaboration shows (not FFPLLC property) aren&apos;t ranked.
      </p>

      <div className="flex flex-wrap gap-px bg-[var(--ff-border)] mb-6" data-testid="leaderboard-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === sort;
          return (
            <button
              key={t.key}
              onClick={() => setSort(t.key)}
              data-testid={`leaderboard-tab-${t.key}`}
              className={`flex items-center gap-2 px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors ${
                isActive ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)] hover:text-white"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-[var(--ff-text-dim)] mb-8 max-w-3xl" data-testid="leaderboard-caption">{active.caption}</p>

      {loading ? (
        <div className="p-16 text-center text-[var(--ff-text-dim)] font-mono-ui text-xs uppercase tracking-[0.2em]">Loading leaderboard…</div>
      ) : rows.length === 0 ? (
        <div className="surface-card p-16 text-center text-[var(--ff-text-dim)] font-mono-ui text-xs uppercase tracking-[0.2em]" data-testid="leaderboard-empty">
          No shows ranked yet — shows appear here once management files their first report card.
        </div>
      ) : (
        <div className="grid gap-px bg-[var(--ff-border)]" data-testid="leaderboard-rows">
          {rows.map((r, i) => <LeaderboardRow key={r.id} row={r} position={i} tab={active} />)}
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({ row, position, tab }) {
  const color = CATEGORY_COLORS[row.category] || "#FF3B30";
  const primary = tab.field
    ? { label: tab.short, value: fmtMetric(tab, row[tab.field]) }
    : { label: "Score", value: Math.round(row.overall_score * 100) };
  const cols = STAT_COLS.filter((k) => k !== tab.key).map((k) => TABS.find((t) => t.key === k));
  return (
    <Link
      to={`/shows/${row.id}`}
      className="bg-[var(--ff-surface)] hover:bg-[var(--ff-surface-hover)] transition-colors grid grid-cols-[auto_60px_1fr_auto] md:grid-cols-[auto_80px_1fr_auto_auto_auto] gap-4 md:gap-6 items-center p-4 md:p-6"
      data-testid={`leaderboard-row-${row.id}`}
    >
      <div className="font-display text-3xl md:text-4xl w-10 md:w-14 text-center" style={{ color: medalColor(position) }} data-testid={`leaderboard-rank-${row.id}`}>
        {String(position + 1).padStart(2, "0")}
      </div>
      <div className="relative aspect-square w-14 md:w-16 overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}44, ${color}11)` }}>
        {row.thumbnail_url ? (
          <img src={row.thumbnail_url} alt={row.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center font-display text-xl opacity-40" style={{ color }}>
            {row.title?.split(" ").map((w) => w[0]).slice(0, 3).join("")}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-lg md:text-2xl truncate" data-testid={`leaderboard-title-${row.id}`}>{row.title}</div>
          {row.is_trending && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--ff-red)]/15 border border-[var(--ff-red)]/40 text-[var(--ff-red)] font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] shrink-0"
              title={`Views grew ${row.view_growth_pct}% in the last 7 days`} data-testid={`leaderboard-trending-${row.id}`}>
              <Flame size={11} /> Trending +{Math.round(row.view_growth_pct)}%
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[var(--ff-text-dim)]">
          {row.category && <span className="font-mono-ui uppercase tracking-[0.15em]" style={{ color }}>{row.category}</span>}
          {row.hosts?.length > 0 && (
            <span className="font-mono-ui uppercase tracking-[0.15em] hidden md:inline">
              w/ {row.hosts.slice(0, 2).join(" · ")}{row.hosts.length > 2 ? ` +${row.hosts.length - 2}` : ""}
            </span>
          )}
          {row.report_period_end && (
            <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] inline-flex items-center gap-1" data-testid={`leaderboard-report-${row.id}`}>
              <ClipboardCheck size={10} /> {reportLabel(row)}
            </span>
          )}
        </div>
      </div>
      {cols.map((c) => (
        <div key={c.key} className="hidden md:flex flex-col items-end min-w-[80px]">
          <div className="label-eyebrow">{c.short}</div>
          <div className="font-display text-2xl" data-testid={`leaderboard-${c.key}-${row.id}`}>{fmtMetric(c, row[c.field])}</div>
        </div>
      )).slice(0, 2)}
      <div className="flex flex-col items-end min-w-[70px]">
        <div className="label-eyebrow text-[var(--ff-red)]">{primary.label}</div>
        <div className="font-display text-3xl" data-testid={`leaderboard-metric-${row.id}`}>{primary.value}</div>
      </div>
    </Link>
  );
}
